import React, { useEffect, useMemo, useState } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import {
  PROPOSAL_BUNDLE_CATEGORY,
  PROPOSAL_BUNDLE_STATE,
  TICKET_CATEGORY,
  type PreferredTransportType,
  type ProposalBundle,
  type ProposalBundleCategory,
  type ProposalItem,
  type TicketCategory
} from '../../types/proposalBundles';
import { formatTransportRouteInfo } from '../../utils/routeInfoFormatter';
import { ForeignFlightProposalTransportTable } from './ForeignFlightProposalTransportTable';
import { TicketCategorySelector } from './TicketCategorySelector';
import { TransportCategoryIcon } from './TransportCategoryIcon';
import { TransportRow } from './TransportRow';
import { TransportTypeToggle } from './TransportTypeToggle';
import type { FeedbackType } from '../../types/messages';
import ProposalBundleFeedback from './ProposalBundleFeedback';

const DEFAULT_PREFERRED_TRANSPORT_TYPE: PreferredTransportType = 'rail';
const TRANSPORT_TYPE = {
  RAIL: 'rail',
  AIR: 'air'
} as const;

type CategoryLabels = { default: string } & Partial<Record<ProposalBundleCategory, string>>;
const CATEGORY_LABELS_BY_TYPE: Record<PreferredTransportType, CategoryLabels> = {
  rail: {
    [PROPOSAL_BUNDLE_CATEGORY.OUTWARD]: '行きの新幹線',
    [PROPOSAL_BUNDLE_CATEGORY.HOMEWARD]: '帰りの新幹線',
    [PROPOSAL_BUNDLE_CATEGORY.TRANSPORT]: '新幹線',
    default: '新幹線'
  },
  air: {
    [PROPOSAL_BUNDLE_CATEGORY.OUTWARD]: '行きの航空券',
    [PROPOSAL_BUNDLE_CATEGORY.HOMEWARD]: '帰りの航空券',
    [PROPOSAL_BUNDLE_CATEGORY.TRANSPORT]: '交通機関',
    default: '交通機関'
  }
};

const getTransportCardCategoryLabel = (
  category: ProposalBundleCategory,
  preferredType: PreferredTransportType | null
): string => {
  const type: PreferredTransportType =
    preferredType === TRANSPORT_TYPE.RAIL ? TRANSPORT_TYPE.RAIL : TRANSPORT_TYPE.AIR;
  const labels = CATEGORY_LABELS_BY_TYPE[type];
  return labels[category] ?? labels.default;
};

const filterItemsByTicketCategory = (items: ProposalItem[], category: TicketCategory): ProposalItem[] =>
  items.filter(item => item.ticketCategory === category);

const hasItemsForCategory = (items: ProposalItem[], category: TicketCategory): boolean =>
  items.some(item => item.ticketCategory === category);

const filterItemsByPreferredType = (
  items: ProposalItem[],
  preferredType: PreferredTransportType | null
): ProposalItem[] => {
  if (preferredType === TRANSPORT_TYPE.RAIL) {
    return items.filter(item => item.isShinkansen === true);
  }
  if (preferredType === TRANSPORT_TYPE.AIR) {
    return items.filter(item => item.isAir === true);
  }
  return items;
};

const computeShowForeignFlightAirDetailTable = (
  displayTransportType: PreferredTransportType | null,
  bundle: Pick<ProposalBundle, 'hasForeignFlight' | 'hasForeignFlightRoundTrip'>
): boolean =>
  displayTransportType === TRANSPORT_TYPE.AIR &&
  (Boolean(bundle.hasForeignFlight) || Boolean(bundle.hasForeignFlightRoundTrip));

const computeShowRoundTripAirPriceHeader = (
  displayTransportType: PreferredTransportType | null,
  bundle: Pick<ProposalBundle, 'category' | 'hasForeignFlight' | 'hasForeignFlightRoundTrip'>
): boolean => {
  if (displayTransportType !== TRANSPORT_TYPE.AIR) return false;
  const hasForeignFlight = Boolean(bundle.hasForeignFlight);
  const hasForeignFlightRoundTrip = Boolean(bundle.hasForeignFlightRoundTrip);
  const isOutwardOrHomeward =
    bundle.category === PROPOSAL_BUNDLE_CATEGORY.OUTWARD || bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOMEWARD;
  const isForeignFlightOneWayOnly = hasForeignFlight && !hasForeignFlightRoundTrip;
  return isOutwardOrHomeward && !isForeignFlightOneWayOnly;
};

interface Props {
  bundle: ProposalBundle;
  productNumber: number;
  includeTax: boolean;
  onSelect?: (bundleId: number, itemId: number) => void;
  onClose?: (bundleId: number) => void;
  onFeedbackUpdate?: (bundleId: number, feedback: FeedbackType, commentSubmitted: boolean) => void;
  disabled?: boolean;
  tripId: number;
  isSpLayout?: boolean;
}

const TransportCard = ({
  bundle,
  productNumber,
  includeTax,
  onSelect,
  onClose,
  onFeedbackUpdate,
  disabled = false,
  tripId,
  isSpLayout = false
}: Props) => {
  const { theme } = useAITTheme();
  const isRejected = bundle.state === PROPOSAL_BUNDLE_STATE.REJECTED;
  const styles = useMemo(() => getStyles(theme, isRejected), [theme, isRejected]);

  const preferredTransportType = bundle.preferredTransportType;
  const effectivePreferredType = preferredTransportType ?? DEFAULT_PREFERRED_TRANSPORT_TYPE;
  const [selectedTransportType, setSelectedTransportType] =
    useState<PreferredTransportType>(effectivePreferredType);

  useEffect(() => {
    setSelectedTransportType(effectivePreferredType);
  }, [effectivePreferredType]);

  const hasRailItems = useMemo(() => bundle.items.some(item => item.isShinkansen === true), [bundle.items]);
  const hasAirItems = useMemo(() => bundle.items.some(item => item.isAir === true), [bundle.items]);
  const showTransportTypeToggle = hasRailItems && hasAirItems;
  const displayTransportType = showTransportTypeToggle
    ? selectedTransportType
    : hasRailItems
    ? TRANSPORT_TYPE.RAIL
    : TRANSPORT_TYPE.AIR;

  const transportItems = useMemo(
    () => filterItemsByPreferredType(bundle.items, displayTransportType),
    [bundle.items, displayTransportType]
  );
  const hasChangeable = useMemo(
    () => hasItemsForCategory(transportItems, TICKET_CATEGORY.CHANGEABLE),
    [transportItems]
  );
  const hasUnchangeable = useMemo(
    () => hasItemsForCategory(transportItems, TICKET_CATEGORY.UNCHANGEABLE),
    [transportItems]
  );
  const hasTicketCategorySplit = hasChangeable && hasUnchangeable;

  const defaultCategory: TicketCategory = hasChangeable
    ? TICKET_CATEGORY.CHANGEABLE
    : TICKET_CATEGORY.UNCHANGEABLE;
  const [selectedTicketCategory, setSelectedTicketCategory] = useState<TicketCategory>(defaultCategory);

  useEffect(() => {
    if (displayTransportType !== TRANSPORT_TYPE.AIR) return;

    if (selectedTicketCategory === TICKET_CATEGORY.CHANGEABLE && hasChangeable === false && hasUnchangeable) {
      setSelectedTicketCategory(TICKET_CATEGORY.UNCHANGEABLE);
      return;
    }

    if (selectedTicketCategory === TICKET_CATEGORY.UNCHANGEABLE && hasUnchangeable === false && hasChangeable) {
      setSelectedTicketCategory(TICKET_CATEGORY.CHANGEABLE);
    }
  }, [displayTransportType, hasChangeable, hasUnchangeable, selectedTicketCategory]);

  const filteredItems = useMemo(
    () =>
      displayTransportType === TRANSPORT_TYPE.RAIL
        ? transportItems
        : hasTicketCategorySplit
        ? filterItemsByTicketCategory(transportItems, selectedTicketCategory)
        : transportItems,
    [displayTransportType, transportItems, selectedTicketCategory, hasTicketCategorySplit]
  );

  const hasForeignFlightRoundTrip = Boolean(bundle.hasForeignFlightRoundTrip);

  const categoryLabel = getTransportCardCategoryLabel(bundle.category, displayTransportType);
  const showRoundTripAirPriceHeader = computeShowRoundTripAirPriceHeader(displayTransportType, bundle);
  const showForeignFlightAirDetailTable = computeShowForeignFlightAirDetailTable(displayTransportType, bundle);
  const showTicketCategoryColumn = filteredItems.some(item => item.ticketCategory != null);
  const routeInfo = useMemo(
    () => formatTransportRouteInfo(bundle.origin, bundle.destination, bundle.departureDate),
    [bundle.origin, bundle.destination, bundle.departureDate]
  );

  const handleSelect = (itemId: number) => {
    onSelect?.(bundle.id, itemId);
  };

  const handleClose = () => {
    onClose?.(bundle.id);
  };

  const handleTicketCategoryChange = (category: TicketCategory) => {
    setSelectedTicketCategory(category);
  };

  const showTicketCategorySelector =
    displayTransportType === TRANSPORT_TYPE.AIR && hasChangeable && hasUnchangeable;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.headerTitleGroup}>
          <span style={styles.productBadge}>商品{productNumber}</span>
          <span style={styles.categoryLabel}>
            <span style={styles.categoryLabelMain}>
              <span style={styles.categoryIcon}>
                <TransportCategoryIcon preferredType={displayTransportType} />
              </span>
              <span>{routeInfo ? `${categoryLabel}：` : categoryLabel}</span>
            </span>
            {routeInfo ? <span style={styles.categoryRoute}>{routeInfo.slice(1)}</span> : null}
          </span>
          {isRejected && <span style={styles.rejectedLabel}>予約しない</span>}
        </div>
        <div style={styles.headerActions}>
          {onFeedbackUpdate && (
            <ProposalBundleFeedback
              bundleId={bundle.id}
              tripId={tripId}
              currentFeedback={bundle.feedback}
              commentSubmitted={bundle.feedbackCommentSubmitted}
              onFeedbackUpdate={onFeedbackUpdate}
            />
          )}
          {!isRejected && (
            <button
              type="button"
              style={{
                ...styles.closeButton,
                ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {})
              }}
              onClick={handleClose}
              disabled={disabled}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {showTicketCategorySelector && (
        <TicketCategorySelector
          bundleId={bundle.id}
          selectedCategory={selectedTicketCategory}
          onChange={handleTicketCategoryChange}
          theme={theme}
          isSpLayout={isSpLayout}
        />
      )}

      {showForeignFlightAirDetailTable ? (
        <ForeignFlightProposalTransportTable
          items={filteredItems}
          showRoundTripAirPriceHeader={showRoundTripAirPriceHeader}
          includeTax={includeTax}
          onSelect={handleSelect}
          disabled={disabled}
          hasRoundTripPair={hasForeignFlightRoundTrip}
          bundleCategory={bundle.category}
          routeCaption={formatTransportRouteInfo(bundle.origin, bundle.destination, bundle.departureDate)}
        />
      ) : isSpLayout ? (
        <div style={styles.spList}>
          {filteredItems.map((item, index) => (
            <TransportRow
              key={item.id}
              item={item}
              index={index}
              includeTax={includeTax}
              onSelect={() => handleSelect(item.id)}
              theme={theme}
              disabled={disabled}
              showTicketCategoryColumn={showTicketCategoryColumn}
              departureDate={bundle.departureDate}
              isSpLayout
            />
          ))}
        </div>
      ) : (
        <div>
          <table style={{ ...styles.table, tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: showTicketCategoryColumn ? '18%' : '22%' }} />
              <col style={{ width: showTicketCategoryColumn ? '32%' : '38%' }} />
              {showTicketCategoryColumn ? <col style={{ width: '18%' }} /> : null}
              <col style={{ width: showTicketCategoryColumn ? '32%' : '40%' }} />
            </colgroup>
            <thead>
              <tr style={styles.tableHeaderRow}>
                <th style={styles.tableHeaderCell}>時間</th>
                <th style={styles.tableHeaderCell}>便名・クラス</th>
                {showTicketCategoryColumn ? <th style={styles.tableHeaderCell}>変更可否</th> : null}
                <th style={styles.tableHeaderCell}>{showRoundTripAirPriceHeader ? '料金（往復）' : '料金'}</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item, index) => (
                <TransportRow
                  key={item.id}
                  item={item}
                  index={index}
                  includeTax={includeTax}
                  onSelect={() => handleSelect(item.id)}
                  theme={theme}
                  disabled={disabled}
                  showTicketCategoryColumn={showTicketCategoryColumn}
                  departureDate={bundle.departureDate}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showTransportTypeToggle && (
        <div style={styles.switchFooter}>
          <TransportTypeToggle
            value={selectedTransportType}
            onChange={setSelectedTransportType}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

const getStyles = (theme: AITTheme, isRejected: boolean) => ({
  card: {
    backgroundColor: theme.whiteColor,
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    border: `2px solid ${isRejected ? theme.grayBorderColor : theme.linkColor}`,
    position: 'relative' as const,
    opacity: isRejected ? 0.6 : 1
  },
  closeButton: {
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: 'none',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    fontSize: '18px',
    fontWeight: 300 as const,
    lineHeight: 1,
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  header: {
    fontWeight: 700 as const,
    color: isRejected ? theme.grayTextColor : theme.linkColor,
    marginBottom: '16px',
    fontSize: '17px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px'
  },
  headerTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    flex: 1
  },
  productBadge: {
    backgroundColor: isRejected ? theme.grayTextColor : theme.linkColor,
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600 as const,
    flexShrink: 0
  },
  categoryLabel: {
    fontSize: '14px',
    display: 'inline-flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    gap: '2px',
    textDecoration: isRejected ? 'line-through' : 'none'
  },
  categoryLabelMain: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  categoryRoute: {
    fontSize: '13px',
    fontWeight: 'bold' as const
  },
  categoryIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    flexShrink: 0
  },
  rejectedLabel: {
    backgroundColor: '#f5f5f5',
    color: theme.grayTextColor,
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '11px',
    fontWeight: 600 as const
  },
  switchFooter: {
    marginTop: '12px',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const
  },
  tableHeaderRow: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #ececec'
  },
  tableHeaderCell: {
    padding: '10px',
    textAlign: 'center' as const,
    fontSize: '13px'
  },
  spList: {
    display: 'flex',
    flexDirection: 'column' as const
  }
});

export default TransportCard;
